import { motion } from 'framer-motion'
import { Tag } from './ui'
import { useT } from '../lib/i18n'
import type { PlaybookBlock, PlaybookSection } from '../lib/locales'

interface PlaybookPageProps {
  onBackToMethod: () => void
}

export function PlaybookPage({ onBackToMethod }: PlaybookPageProps) {
  return (
    <main>
      <PlaybookHero onBackToMethod={onBackToMethod} />
      <PlaybookSections />
    </main>
  )
}

function PlaybookHero({ onBackToMethod }: { onBackToMethod: () => void }) {
  const h = useT().playbook.hero
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 pt-20 sm:pt-28 pb-10 sm:pb-14">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <Tag kind="rule" className="text-[length:var(--text-micro)]">
          {h.tag}
        </Tag>
        <button
          type="button"
          onClick={onBackToMethod}
          className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] transition-colors"
        >
          {h.backToMethodLabel}
        </button>
      </div>
      <motion.h1
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="text-[color:var(--color-orion-text)] leading-[1.05] max-w-4xl"
        style={{ fontSize: 'var(--text-h1)', fontWeight: 600 }}
      >
        {h.title}
      </motion.h1>
      <p className="mt-4 mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
        {h.subtitle}
      </p>
      <p
        className="mt-6 max-w-3xl text-[color:var(--color-orion-haze)]"
        style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}
      >
        {h.leadParagraph}
      </p>
    </section>
  )
}

function PlaybookSections() {
  const sections = useT().playbook.sections
  return (
    <div className="mx-auto max-w-6xl px-6 sm:px-10">
      {sections.map((section, idx) => (
        <SectionView key={section.id} section={section} idx={idx} />
      ))}
    </div>
  )
}

function SectionView({ section, idx }: { section: PlaybookSection; idx: number }) {
  return (
    <motion.section
      id={section.id}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{ duration: 0.35, delay: Math.min(idx * 0.02, 0.2) }}
      className="py-10 sm:py-14 border-t border-[color:var(--color-orion-surface)]/40 first:border-t-0"
    >
      <div className="flex items-baseline gap-4 mb-4 flex-wrap">
        <span className="mono text-[length:var(--text-h3)] text-[color:var(--color-orion-haze)] font-light">
          {section.number}
        </span>
        <h2
          className="text-[color:var(--color-orion-text)] leading-[1.15]"
          style={{ fontSize: 'var(--text-h2)', fontWeight: 600 }}
        >
          {section.title}
        </h2>
      </div>
      {section.lead && (
        <p
          className="mb-6 max-w-3xl text-[color:var(--color-orion-haze)]"
          style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}
        >
          {section.lead}
        </p>
      )}
      <div className="space-y-5">
        {section.blocks.map((block, i) => (
          <BlockView key={i} block={block} />
        ))}
      </div>
    </motion.section>
  )
}

function BlockView({ block }: { block: PlaybookBlock }) {
  switch (block.kind) {
    case 'text':
      return (
        <p className="text-[color:var(--color-orion-text)] max-w-3xl leading-relaxed">{block.body}</p>
      )
    case 'bullets':
      return (
        <ul className="space-y-2 max-w-3xl">
          {block.bullets.map((b, i) => (
            <li key={i} className="flex items-baseline gap-3 text-[color:var(--color-orion-text)]">
              <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-amber)] shrink-0">·</span>
              <span className="leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      )
    case 'numberedBullets':
      return (
        <ol className="grid sm:grid-cols-3 gap-4">
          {block.items.map((item, i) => (
            <li key={i} className="surface-night border-faint rounded-[var(--radius-lg)] p-5">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="mono text-[24px] leading-none text-[color:var(--color-orion-haze)] font-light">
                  0{i + 1}
                </span>
                <div className="text-[color:var(--color-orion-text)] font-medium" style={{ fontSize: 'var(--text-h4)' }}>
                  {item.title}
                </div>
              </div>
              <p className="text-[color:var(--color-orion-haze)] text-[length:var(--text-small)] leading-relaxed">
                {item.body}
              </p>
            </li>
          ))}
        </ol>
      )
    case 'table2col':
      return (
        <div className="overflow-x-auto surface-night border-faint rounded-[var(--radius-lg)]">
          <table className="w-full text-[length:var(--text-small)]">
            <thead>
              <tr className="border-b border-[color:var(--color-orion-surface)]/40">
                <th className="text-left px-4 py-3 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] w-[30%]">
                  {block.headers[0]}
                </th>
                <th className="text-left px-4 py-3 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
                  {block.headers[1]}
                </th>
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className="border-b border-[color:var(--color-orion-surface)]/20 last:border-b-0">
                  <td className="px-4 py-3 text-[color:var(--color-orion-text)] font-medium align-top">{row[0]}</td>
                  <td className="px-4 py-3 text-[color:var(--color-orion-haze)]">{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case 'table3col':
      return (
        <div className="overflow-x-auto surface-night border-faint rounded-[var(--radius-lg)]">
          <table className="w-full text-[length:var(--text-small)]">
            <thead>
              <tr className="border-b border-[color:var(--color-orion-surface)]/40">
                <th className="text-left px-4 py-3 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
                  {block.headers[0]}
                </th>
                <th className="text-left px-4 py-3 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
                  {block.headers[1]}
                </th>
                <th className="text-left px-4 py-3 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
                  {block.headers[2]}
                </th>
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className="border-b border-[color:var(--color-orion-surface)]/20 last:border-b-0">
                  <td className="px-4 py-3 text-[color:var(--color-orion-text)] font-medium align-top">{row[0]}</td>
                  <td className="px-4 py-3 text-[color:var(--color-orion-haze)]">{row[1]}</td>
                  <td className="px-4 py-3 text-[color:var(--color-orion-text)]">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case 'callout':
      return (
        <div className="surface-night border-faint border-l-2 border-l-[color:var(--color-orion-amber)] rounded-[var(--radius-lg)] p-5 sm:p-6 max-w-4xl">
          {block.tag && (
            <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
              {block.tag}
            </div>
          )}
          {block.title && (
            <div className="text-[color:var(--color-orion-text)] font-medium mb-2" style={{ fontSize: 'var(--text-h4)' }}>
              {block.title}
            </div>
          )}
          <p className="text-[color:var(--color-orion-haze)] leading-relaxed">{block.body}</p>
        </div>
      )
    case 'checklist':
      return (
        <ul className="grid sm:grid-cols-2 gap-3 max-w-4xl">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="surface-night border-faint rounded-[var(--radius-md)] p-4 flex items-baseline gap-3"
            >
              <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-amber)] shrink-0">
                ☐
              </span>
              <span className="text-[color:var(--color-orion-text)] text-[length:var(--text-small)]">{item}</span>
            </li>
          ))}
        </ul>
      )
  }
}
